import { useState, useEffect } from "react";
import { useInquiry } from "../context/InquiryContext";
import { createInquiry } from "../services/inquiryService";
import Swal from "sweetalert2";

const UNITS = ["Kg", "Ton", "Unit", "Box", "Carton"];
const COUNTRIES = [
  "India",
  "United States",
  "United Arab Emirates",
  "United Kingdom",
  "China",
  "Other",
];

// Static list of 10 country codes (not fetched dynamically)
const COUNTRY_CODES = [
  { code: "+91", label: "+91 (India)" },
  { code: "+1", label: "+1 (USA/Canada)" },
  { code: "+971", label: "+971 (UAE)" },
  { code: "+44", label: "+44 (UK)" },
  { code: "+86", label: "+86 (China)" },
  { code: "+61", label: "+61 (Australia)" },
  { code: "+49", label: "+49 (Germany)" },
  { code: "+33", label: "+33 (France)" },
  { code: "+81", label: "+81 (Japan)" },
  { code: "+65", label: "+65 (Singapore)" },
];

// step: 1 = requirements, 2 = contact details, 3 = success
export default function InquiryModal() {
  const { isOpen, product, closeInquiry } = useInquiry();
  const [step, setStep] = useState(1);
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState("");
  const [country, setCountry] = useState("");
  const [remarks, setRemarks] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  // Check if user has touched any field
  const isDirty =
    fullName !== "" ||
    companyName !== "" ||
    email !== "" ||
    countryCode !== "+91" ||
    phone !== "" ||
    unit !== "" ||
    country !== "" ||
    remarks !== "" ||
    files.length > 0;

  // ESC key listener
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (step === 3) {
          resetAndClose();
        } else if (isDirty) {
          setShowConfirm(true);
        } else {
          resetAndClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isDirty, step]);

  if (!isOpen || !product) return null;

  const resetAndClose = () => {
    setStep(1);
    setFullName("");
    setCompanyName("");
    setEmail("");
    setCountryCode("+91");
    setPhone("");
    setQuantity(1);
    setUnit("");
    setCountry("");
    setRemarks("");
    setFiles([]);
    setError("");
    setShowConfirm(false);
    closeInquiry();
  };

  const handleAttemptClose = () => {
    if (step === 3) {
      resetAndClose();
      return;
    }
    if (isDirty) {
      setShowConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const showMessage = (msg = "", type = "success") => {
    const toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
    });
    toast.fire({ icon: type, title: msg });
  };

  const handleStep1Continue = () => {
    if (!quantity || !unit || !country || !remarks) {
      showMessage("Please fill all required fields.", "warning");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!fullName || !email || !countryCode || !phone) {
      showMessage("Please fill all required fields.", "warning");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("product_id", product.product_id);
      formData.append("full_name", fullName);
      if (companyName) {
        formData.append("company_name", companyName);
      }
      formData.append("email", email);
      formData.append("country_code", countryCode);
      formData.append("phone", phone);
      formData.append("quantity", quantity);
      formData.append("unit", unit);
      formData.append("country", country);
      formData.append("remarks", remarks);
      files.forEach((f) => formData.append("attachments[]", f));

      await createInquiry(formData);
      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to send inquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  const productImg = product.img1 || (product.images?.[0]
    ? (product.images[0].image.startsWith("http")
        ? product.images[0].image
        : `http://localhost:8000/storage/${product.images[0].image}`)
    : "/img/product/placeholder.jpg");

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 px-2 sm:px-4"
      onClick={handleAttemptClose}
    >
      <div
        className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6 relative animate-[fadeIn_0.2s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleAttemptClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-dark text-xl leading-none"
        >
          &times;
        </button>

        {/* ── STEP 1: Product Requirements ── */}
        {step === 1 && (
          <>
            {/* Back label (just decorative on step 1) */}
            <p className="text-xs text-primary font-medium mb-4 cursor-default">Send Product Inquiry</p>

            <h2 className="text-lg sm:text-xl font-bold text-dark mb-5">Product Requirements</h2>

            {/* Product preview */}
            <div className="flex items-center gap-3 bg-gray-50 p-3 mb-6">
              <img
                src={productImg}
                alt={product.name}
                className="w-12 h-12 sm:w-14 sm:h-14 object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-dark leading-snug line-clamp-2">{product.name}</p>
                <p className="text-xs text-gray-400 mt-1">MOQ: 1 Unit</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">
                Quantity Required *
              </label>
              {/* Stack vertically on very small screens, side-by-side on sm+ */}
              <div className="flex flex-col xs:flex-row gap-2">
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="w-full xs:flex-1 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
                />
                <select
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  className="w-full xs:flex-1 border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
                >
                  <option value="">Select Unit</option>
                  {UNITS.map((u) => (
                    <option key={u} value={u}>{u}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">
                Delivery Location *
              </label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
              >
                <option value="">Select country</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">
                Requirements / Remarks *
              </label>
              <textarea
                rows={4}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Specify any special requirements, delivery timeline, or additional details..."
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary resize-none"
              />
            </div>

            <div className="mb-6">
              <label className="text-xs font-medium text-gray-500 block mb-1">
                Attach Files (Optional)
              </label>
              <label className="border border-dashed border-gray-300 flex items-center justify-center gap-2 py-3 text-xs text-gray-400 cursor-pointer hover:border-primary">
                <input
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => setFiles(Array.from(e.target.files))}
                />
                Upload images or files
              </label>
              {files.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">{files.length} file(s) selected</p>
              )}
            </div>

            <button
              onClick={handleStep1Continue}
              className="w-full bg-dark text-white text-sm font-semibold py-3 btn-readmore hover:text-black transition-colors"
            >
              Continue to Send Inquiry
            </button>
          </>
        )}

        {/* ── STEP 2: Contact Details ── */}
        {step === 2 && (
          <>
            <button
              onClick={() => { setError(""); setStep(1); }}
              className="flex items-center gap-1 text-xs text-primary font-medium mb-4 hover:underline"
            >
              ← Back
            </button>

            <h2 className="text-lg sm:text-xl font-bold text-dark mb-1">Send Product Inquiry</h2>
            <p className="text-xs text-gray-500 mb-5">Your Contact Information</p>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">Name *</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">Company Name *</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Enter your company name"
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@company.com"
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            <div className="mb-4">
              <label className="text-xs font-medium text-gray-500 block mb-1">Country Code *</label>
              <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="text-xs font-medium text-gray-500 block mb-1">Mobile Number *</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter mobile number"
                className="w-full border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </div>

            {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-dark text-white text-sm font-semibold py-3 btn-readmore hover:text-black transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Submit Inquiry"}
            </button>
          </>
        )}

        {/* ── STEP 3: Success ── */}
        {step === 3 && (
          <div className="text-center py-6">
            {/* Green check circle */}
            <div className="w-16 h-16 rounded-full border-4 border-green-400 flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h2 className="text-lg sm:text-xl font-bold text-dark mb-2">Inquiry Submitted Successfully!</h2>
            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Thank you for your interest. We have received your inquiry and our team will get back to you shortly.
            </p>

            {/* Product summary card */}
            <div className="flex items-center gap-3 bg-gray-50 border border-border-color p-3 text-left mb-6">
              <img
                src={productImg}
                alt={product.name}
                className="w-12 h-12 sm:w-14 sm:h-14 object-cover flex-shrink-0"
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-dark leading-snug line-clamp-2">{product.name}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Quantity Required: {quantity} {unit}
                </p>
              </div>
            </div>

            <button
              onClick={resetAndClose}
              className="w-full bg-dark text-white text-sm font-semibold py-3 hover:bg-primary transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>

      {/* ── Confirmation Dialog ── */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 px-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white w-full max-w-sm p-6 shadow-xl">
            <h3 className="text-base font-bold text-dark mb-2">Discard Inquiry?</h3>
            <p className="text-sm text-gray-500 mb-6">
              You have unsaved information. Are you sure you want to close and discard your inquiry?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 border border-gray-300 text-sm font-semibold py-2.5 text-dark hover:bg-gray-50 transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={resetAndClose}
                className="flex-1 bg-dark text-white text-sm font-semibold py-2.5 hover:bg-primary transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}